import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox.tsx';
import type { FilterConfig } from '@openx/types/chipFilters.ts';
import { parseMultiValueFilter, serializeMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DealListDealTypePill } from './DealListDealTypePill.tsx';
import {
  findFilterOptionByValue,
  getMoreFiltersSelectableOptionSx,
  getVisibleFilterOptions,
} from './dealsMoreFiltersShared.ts';

export interface DealsMoreFiltersDealTypeFieldProps {
  filterConfig: FilterConfig;
  draftValue: string;
  onDraftChange: (value: string) => void;
  dataTest?: string;
}

export function DealsMoreFiltersDealTypeField({
  filterConfig,
  draftValue,
  onDraftChange,
  dataTest = 'deals-more-filters-deal-type-section',
}: DealsMoreFiltersDealTypeFieldProps) {
  const { t } = useTranslation();
  const { multiple, options = [] } = filterConfig;

  const visibleOptions = useMemo(() => getVisibleFilterOptions(options), [options]);
  const selectedValues = useMemo(
    () => (multiple ? parseMultiValueFilter(draftValue) : draftValue ? [draftValue] : []),
    [draftValue, multiple]
  );

  const handleClearSelected = useCallback(() => {
    onDraftChange('');
  }, [onDraftChange]);

  const toggleValue = useCallback(
    (value: string) => {
      if (multiple) {
        const nextValues = selectedValues.includes(value)
          ? selectedValues.filter(selectedValue => selectedValue !== value)
          : [...selectedValues, value];

        onDraftChange(serializeMultiValueFilter(nextValues));
        return;
      }

      onDraftChange(draftValue === value ? '' : value);
    },
    [draftValue, multiple, onDraftChange, selectedValues]
  );

  return (
    <Stack
      direction="column"
      data-test={dataTest}
      sx={{
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component="label" variant="body1Bold">
          {filterConfig.name}
        </Typography>
        {selectedValues.length > 0 && (
          <Button variant="text" size="small" onClick={handleClearSelected} sx={{ minWidth: 0, px: 0 }}>
            {t('Clear Selected')}
          </Button>
        )}
      </Box>
      <Stack
        direction="column"
        sx={{
          gap: 0.25,
        }}
      >
        {visibleOptions.map(option => {
          const optionValue = String(option.value ?? '');
          const resolvedOption = findFilterOptionByValue(filterConfig, optionValue);
          const label = String(resolvedOption?.displayName ?? resolvedOption?.value ?? optionValue);
          const isChecked = selectedValues.includes(optionValue);

          return (
            <FormControlLabel
              key={optionValue}
              control={<Checkbox checked={isChecked} onChange={() => toggleValue(optionValue)} size="small" />}
              label={<DealListDealTypePill label={label} value={optionValue} />}
              data-test={`deal-type-option-${optionValue}`}
              sx={getMoreFiltersSelectableOptionSx({
                isChecked,
                minHeight: 30,
                labelStyles: {
                  display: 'flex',
                  alignItems: 'center',
                },
              })}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
